import { LegacyRef, useCallback } from "react"
import { Options } from "@splidejs/splide"
import {
  Splide,
  SplideEventHandlers,
  SplideProps,
} from "@splidejs/react-splide"

import { Box, SxProps, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import isFunction from "lodash/isFunction"

import "@splidejs/react-splide/css"

import { getVisiblePaginationIndexRange } from "utils/carousel"

export interface IExtendOptions extends Options {
  limitPaginations?: number
}
export interface ICarouselProps extends Omit<SplideProps, "options"> {
  children: React.ReactNode
  sx?: SxProps
  options?: IExtendOptions
  splideRef?: LegacyRef<Splide>
}

/* Single source of truth for autoplay reconciliation. Always called after a refresh so
   splide.Components.Autoplay is the live instance (not a stale pre-refresh capture).
   splideRef is optional and its .current is not yet attached while Splide emits `ready`
   from its own componentDidMount, so fall back to the live core options. */
const syncAutoplay = (splide, splideRef) => {
  const autoplay = splide.Components.Autoplay
  if (!autoplay) return
  const options = splideRef?.current?.options ?? splide.options
  if (options.autoplay && splide.length > 0) autoplay.play()
  else autoplay.pause()
}

export const Carousel: React.FC<ICarouselProps> = ({
  children,
  options: optionsProps = {},
  onUpdated: onUpdatedProp,
  sx = {},
  onPaginationUpdated,
  onReady,
  onActive,
  onInactive,
  splideRef,
  ...restProps
}) => {
  const { limitPaginations, ...options } = optionsProps
  const theme = useTheme()
  const { root } = theme.components[`VuiCarousel`]?.styleOverrides || {}
  const isThemeReady = theme.mixins.ready

  const handleReady: SplideEventHandlers["onReady"] = useCallback(
    (splide) => {
      //pause all video that is not in active slide
      splide.root
        .querySelectorAll(".splide__slide:not(.is-active)")
        .forEach((e) => {
          const video = e.querySelector("video")
          if (video) video.pause()
        })

      const activeSlide = splide.root.querySelector(".is-active")
      if (activeSlide) {
        const activeVideo = activeSlide.querySelector("video")
        if (activeVideo) {
          activeVideo.muted = true
          activeVideo.play()
        }
      }
      syncAutoplay(splide, splideRef)
      if (isFunction(onReady)) onReady(splide)
    },
    [onReady, splideRef]
  )

  const handleActive: SplideEventHandlers["onActive"] = useCallback(
    (splide, slide) => {
      const video = slide.slide.querySelector("video")
      if (video) {
        video.muted = true
        video.play()
      }

      if (isFunction(onActive)) onActive(splide, slide)
    },
    [onActive]
  )

  const handleInactive: SplideEventHandlers["onInactive"] = useCallback(
    (splide, slide) => {
      const video = slide.slide.querySelector("video")
      if (video) video.pause()

      if (isFunction(onInactive)) onInactive(splide, slide)
    },
    [onInactive]
  )

  const handlePaginationUpdated: SplideEventHandlers["onPaginationUpdated"] =
    useCallback(
      (splide, ...rest) => {
        if (limitPaginations) {
          const allPaginationButton = splide.root.querySelectorAll(
            " .splide__pagination__page"
          )
          const currentIndex = splide.index
          const totalPaginations = splide.length

          const { startIndex, endIndex } = getVisiblePaginationIndexRange(
            currentIndex,
            totalPaginations,
            limitPaginations
          )

          allPaginationButton.forEach((dot, index) => {
            const shouldShow = index >= startIndex && index <= endIndex
            ;(dot as HTMLElement).style.display = shouldShow
              ? "inline-block"
              : "none"
          })
        }

        if (isFunction(onPaginationUpdated))
          onPaginationUpdated(splide, ...rest)
      },
      [limitPaginations, onPaginationUpdated]
    )

  /* onRefresh fires when slides are dynamically added/removed. Delegate to syncAutoplay so
     autoplay restarts when slides repopulate after an empty state. */
  const handleRefresh: SplideEventHandlers["onRefresh"] = useCallback(
    (splide) => {
      syncAutoplay(splide, splideRef)
    },
    [splideRef]
  )

  const handleOptionsUpdated: SplideEventHandlers["onUpdated"] = useCallback(
    (splide, options) => {
      /* Refresh the carousel so Splide applies the new options and fires onRefresh →
         syncAutoplay, which reconciles autoplay against the updated options.
         [@Doc](https://splidejs.com/guides/options/#autoplay) */
      splide.refresh()
      if (isFunction(onUpdatedProp)) onUpdatedProp(splide, options)
    },
    [onUpdatedProp]
  )

  const direction = theme.direction

  if (!isThemeReady) return null

  return (
    <Box sx={deepmerge(root, sx)}>
      <Splide
        aria-label="Carousel"
        ref={splideRef}
        {...restProps}
        options={{
          rewind: true,
          perPage: 1,
          gap: "5rem",
          padding: { left: 24, right: 24 },
          pagination: false,
          arrows: false,
          direction: direction,
          ...options,
        }}
        onUpdated={handleOptionsUpdated}
        onPaginationUpdated={handlePaginationUpdated}
        onReady={handleReady}
        onActive={handleActive}
        onInactive={handleInactive}
        onRefresh={handleRefresh}
      >
        {children}
      </Splide>
    </Box>
  )
}
